import { useState } from 'react';
import { Flex, Box, Badge, Button, VStack, IconButton, useBreakpointValue } from '@telescope/cassini-ui';
import { useWidget, useClipboard } from '@telescope/cassini-hooks';
import { Text, Heading, Canvas } from 'components';
import { useSpinToWin } from '../../provider';
import { SlotMachineOpen } from 'types';
import { useAuth } from 'features/auth';
import { AiOutlineCopy } from 'react-icons/ai';
import { TbFileCheck } from 'react-icons/tb';
import { SLOT_MACHINE_SIZE } from 'features';

export const ResultsPage = () => {
  const { spinsRemaining, isMultiSpin, voteLimit, handleExtraSpin } = useSpinToWin();
  const { data: results } = useWidget({ select: (data: SlotMachineOpen) => data.snapshot.snapshot_views.results });
  const { content: { winner, loser, topDescription }} = results!;

  const { data } = useWidget({ select: (data: SlotMachineOpen) => data.snapshot.snapshot_views.landing });
  const { content, settings, styles } = data!;

  const { data: slotMachineData } = useWidget({ select: (data: SlotMachineOpen) => data.snapshot.snapshot_views.slotMachine });
  const { styles: slotMachineStyles } = slotMachineData!;

  const [ isLoading, setIsLoading ] = useState(false);
  
  const { user } = useAuth();
  const isWinner = user?.isWinner;
  const prize = user?.prize;

  const resultsContent = isWinner? winner : loser;
  const { prize_content: prizeContent, winner_message: winnerMessage } = prize?.data || {};

  const image = prizeContent?.image?.src || resultsContent.image;

  const displayRemainingSpins = settings.displayRemainingSpins && isMultiSpin && user;
  const spinsRemainingLabel = content.spinsRemaining[spinsRemaining === 1? 'singular' : 'plural']
    ?.replace('{{COUNT}}', `${spinsRemaining}`)
    .replace('{{TOTAL}}', `${voteLimit}`)

  const { onCopy, hasCopied } = useClipboard(prize?.code!);

  const slotMachineSize = useBreakpointValue(SLOT_MACHINE_SIZE)!;

  const handleExtraSpinClick = () => {
    setIsLoading(true);
    handleExtraSpin();
  }

  return (
    <Flex direction="column" align="center" mb={8} maxW="100vw">
      <Box textAlign="center" mb={5} maxW="100%" px={4}>
        <Text allowMarkdown={true} mb={5}>
          {topDescription}
        </Text>

        <Heading size={["md", "xl", "xl", "2xl"]} mb={3} allowMarkdown={true}>{resultsContent.headline}</Heading>

        { isWinner &&
          <>
          <Text fontWeight="900" mb={[3, 3, 4]}> {winnerMessage?.title} </Text>
          <Text mb={[3, 3, 4]} fontSize="sm"> {winnerMessage?.subtitle} </Text>
         </> }
        
        { prize?.code && prize.code !== "undefined" &&
          <Badge fontSize="md" sx={{svg: {margin: 'auto'}}} pl={5} mb={4} textTransform="none">
            {prize?.code}
            <IconButton icon={hasCopied? <TbFileCheck /> : <AiOutlineCopy />} aria-label="copy code" variant="unstyled" onClick={onCopy} />
          </Badge> }

        <Text allowMarkdown={true}>
          {resultsContent.description}
        </Text>
      </Box>

      { isMultiSpin && 
        <VStack spacing={2} mb={5}>
          <Button size={["md", "md", "xl"]} variant="pill" 
                  onClick={handleExtraSpinClick} className="Wheel__spin-button" 
                  isDisabled={!spinsRemaining} isLoading={isLoading} loadingText={content.spinButton.spinAgain}>
            { content.spinButton.spinAgain }
          </Button> 
          { displayRemainingSpins && <Text fontSize="xs"> {spinsRemainingLabel} </Text> }
        </VStack> }

      { image &&
      <Box py={7} position="relative">
        <Box position="absolute" w="100vw" h="100%" top={0} left="50%" transform="translateX(-50%)" maxW={settings.isContainerFullWidth? "100vw" : "app"} bg={styles.container.backgroundColor}>
          <Box position="absolute" w="full" h="100%" top={0} left="50%" transform="translateX(-50%)" sx={{ ...styles.container }} />
        </Box>
        <Flex direction="column" align="center">
          <Box position="relative" w="570px" h="" borderWidth={2} overflow="hidden" maxW="100%"
            sx={{ canvas: { position: 'absolute', width: '100%', height: '100%', ...slotMachineStyles.slotMachine } }}
            {...slotMachineSize}>
            <Canvas 
              image={image} 
              alt={prizeContent?.image?.alt || ''} 
              textStyles={prize?.data?.prize_content.text}
              text={prize?.code} />
          </Box>
        </Flex>
      </Box> }

    </Flex>
  )
}
